import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Scale } from 'lucide-react';
import { getActiveScaleSettings, isScaleConfigured } from '@/store/scaleStore';
import {
  startScaleReading,
  stopScaleReading,
  isScaleTransportAvailable,
} from '@/hooks/useScaleConnection';
import { formatMoney, calcLineAmount } from '@/utils/currency';

interface WeightEntryModalProps {
  itemName: string;
  ratePerUnit: number;
  uom?: string;
  initialWeight?: number;
  onConfirm: (weightKg: number) => void;
  onCancel: () => void;
}

export default function WeightEntryModal({
  itemName,
  ratePerUnit,
  uom = 'Kilogram',
  initialWeight = 0,
  onConfirm,
  onCancel,
}: WeightEntryModalProps) {
  const { t } = useTranslation();
  const [weight, setWeight] = useState(initialWeight > 0 ? String(initialWeight) : '');
  const [scaleReading, setScaleReading] = useState<number | null>(null);
  const [scaleLive, setScaleLive] = useState(false);

  useEffect(() => {
    const transport = isScaleTransportAvailable();
    const settings = getActiveScaleSettings();
    if (!transport.available || !isScaleConfigured(settings)) return;
    if (settings.protocol === 'keyboard_wedge') return;
    if (!settings.autoConnect) return;

    let cancelled = false;
    const start = async () => {
      const res = await startScaleReading((r) => {
        if (!cancelled) setScaleReading(r.kg);
      });
      if (!cancelled && res.ok) setScaleLive(true);
    };
    void start();
    return () => {
      cancelled = true;
      void stopScaleReading();
    };
  }, []);

  const parsed = parseFloat(weight);
  const valid = !Number.isNaN(parsed) && parsed > 0;
  const amount = valid ? calcLineAmount(parsed, ratePerUnit) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Scale className="w-4 h-4 text-primary-600" />
            {t('pos.enterWeight')}
          </h3>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-700 font-medium">{itemName}</p>
          <p className="text-xs text-gray-500">
            {t('pos.pricePerUnit', { rate: formatMoney(ratePerUnit), uom })}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('pos.weightKg')}
            </label>
            <input
              autoFocus
              type="number"
              min="0.001"
              step="0.001"
              className="input-field text-lg font-mono"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && valid) onConfirm(parsed);
              }}
              placeholder="0.000"
            />
          </div>
          {scaleLive && (
            <div className="flex items-center justify-between gap-2 text-sm bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <span className="text-emerald-800">
                {t('pos.scaleReading')}:{' '}
                <strong className="font-mono">
                  {scaleReading != null ? scaleReading.toFixed(3) : '—'} {uom}
                </strong>
              </span>
              <button
                type="button"
                disabled={scaleReading == null || scaleReading <= 0}
                className="text-xs font-medium text-emerald-700 hover:underline disabled:opacity-40"
                onClick={() => {
                  if (scaleReading != null && scaleReading > 0) {
                    setWeight(scaleReading.toFixed(3));
                  }
                }}
              >
                {t('pos.useScaleReading')}
              </button>
            </div>
          )}
          <p className="text-sm text-gray-600">
            {t('common.amount')}: <strong>{formatMoney(amount)}</strong>
          </p>
        </div>
        <div className="flex gap-2 px-4 py-3 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="btn-secondary flex-1">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!valid}
            onClick={() => onConfirm(parsed)}
            className="btn-primary flex-1 disabled:opacity-50"
          >
            {t('pos.addToCart')}
          </button>
        </div>
      </div>
    </div>
  );
}
